import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { siguienteCorte } from '../utils/prestamo';
import { formatearFecha } from '../utils/fecha';

function OverdueLoans() {
  const [loansStatus, setLoansStatus] = useState([]);
  const [filterType, setFilterType] = useState('all'); // all, overdue, warning, active
  const [loansSinFecha, setLoansSinFecha] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoanStatus();
  }, []);

  const fetchLoanStatus = async () => {
    try {
      setLoading(true);
      // Una sola petición con todos los préstamos y su cliente. Antes se pedían
      // uno a uno por cliente: 114 llamadas encadenadas, unos 30 segundos.
      const { data } = await axios.get('/api/loans');
      const loans = data || [];

      const allLoans = [];
      const today = new Date();
      let sinFecha = 0;

      for (const loan of loans) {
        // ── Los préstamos sin fecha de fin también vencen ──
        //
        // Antes esta pantalla los saltaba: `if (!maturity_date) continue`. En
        // la cartera real 193 de 204 préstamos no llevan esa fecha, así que
        // mostraba once de doscientos cuatro y el dueño decía «solo me aparecen
        // los que tienen fecha de vencimiento». Tenía razón, y era literal.
        //
        // Un préstamo abierto no tiene fecha de FIN, pero sí tiene fecha de
        // COBRO: el corte en que se le vence el interés. Esa es la que le
        // importa a quien va a cobrar, y sale del mismo motor que el resto del
        // programa —no se recalcula aquí— para que ninguna pantalla contradiga
        // a otra.
        const abierto = !loan.maturity_date;
        const fecha = abierto ? siguienteCorte(loan, today)
                              : new Date(loan.maturity_date);

        // Ni fecha de fin ni corte calculable: eso sí no se puede clasificar.
        if (!fecha || isNaN(fecha.getTime())) {
          sinFecha++;
          continue;
        }

        const daysUntil = Math.ceil((fecha - today) / (1000 * 60 * 60 * 24));

        let status = 'active';
        if (daysUntil < 0) {
          status = 'overdue';
        } else if (daysUntil <= 7) {
          status = 'warning';
        }

        allLoans.push({
          id: loan.id,
          clientId: loan.client_id,
          clientName: loan.client_name,
          clientPhone: loan.phone,
          amount: loan.amount,
          interestRate: loan.interest_rate,
          startDate: loan.start_date,
          maturityDate: fecha.toISOString(),
          // Para poder decir en pantalla de qué fecha se está hablando: en uno
          // es el fin del préstamo, en el otro el cobro del interés. Llamarlas
          // igual sería mentir sobre dos cosas distintas.
          abierto,
          daysUntil,
          status
        });
      }

      setLoansSinFecha(sinFecha);
      setLoansStatus(allLoans);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching loan status:', err);
      setLoading(false);
    }
  };

  const getFilteredLoans = () => {
    switch (filterType) {
      case 'overdue':
        return loansStatus.filter(l => l.status === 'overdue');
      case 'warning':
        return loansStatus.filter(l => l.status === 'warning');
      case 'active':
        return loansStatus.filter(l => l.status === 'active');
      default:
        return loansStatus;
    }
  };

  const filteredLoans = getFilteredLoans();

  const overdueCount = loansStatus.filter(l => l.status === 'overdue').length;
  const warningCount = loansStatus.filter(l => l.status === 'warning').length;
  const activeCount = loansStatus.filter(l => l.status === 'active').length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'overdue':
        return '🔴';
      case 'warning':
        return '🟡';
      default:
        return '🟢';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      default:
        return '#10b981';
    }
  };

  return (
    <div className="overdue-loans-page">
      <h2>📋 Reporte de Vencimientos</h2>

      {!loading && loansSinFecha > 0 && (
        <div style={{
          padding: '15px', borderRadius: '8px', marginBottom: '20px',
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '14px'
        }}>
          ⚠️ <strong>{loansSinFecha} préstamos</strong> no aparecen aquí: no tienen
          fecha de vencimiento y tampoco se les pudo calcular el próximo cobro
          —normalmente porque les falta la fecha de inicio—. Revíselos en
          Clientes.
        </div>
      )}

      {/* Tarjetas de resumen */}
      <div className="summary-cards">
        <div className="summary-card" onClick={() => setFilterType('all')} style={{ cursor: 'pointer', borderLeft: filterType === 'all' ? '4px solid #667eea' : 'none' }}>
          <div className="card-value">{loansStatus.length}</div>
          <div className="card-label">Total Préstamos</div>
        </div>

        <div className="summary-card" onClick={() => setFilterType('overdue')} style={{ cursor: 'pointer', borderLeft: filterType === 'overdue' ? '4px solid #ef4444' : 'none', backgroundColor: '#fef2f2' }}>
          <div className="card-value" style={{ color: '#ef4444' }}>{overdueCount}</div>
          <div className="card-label">🔴 Vencidos</div>
        </div>

        <div className="summary-card" onClick={() => setFilterType('warning')} style={{ cursor: 'pointer', borderLeft: filterType === 'warning' ? '4px solid #f59e0b' : 'none', backgroundColor: '#fffbeb' }}>
          <div className="card-value" style={{ color: '#f59e0b' }}>{warningCount}</div>
          <div className="card-label">🟡 Por Vencer</div>
        </div>

        <div className="summary-card" onClick={() => setFilterType('active')} style={{ cursor: 'pointer', borderLeft: filterType === 'active' ? '4px solid #10b981' : 'none', backgroundColor: '#f0fdf4' }}>
          <div className="card-value" style={{ color: '#10b981' }}>{activeCount}</div>
          <div className="card-label">🟢 Vigentes</div>
        </div>
      </div>

      {/* Tabla */}
      <div className="table-container">
        <h3>Detalles ({filteredLoans.length})</h3>

        {loading ? (
          <p className="empty-state">Cargando datos...</p>
        ) : filteredLoans.length === 0 ? (
          <p className="empty-state">
            {filterType === 'overdue' && 'No hay préstamos vencidos ✨'}
            {filterType === 'warning' && 'No hay préstamos por vencer en 7 días ✨'}
            {filterType === 'active' && 'No hay préstamos vigentes'}
            {filterType === 'all' && 'No hay préstamos registrados'}
          </p>
        ) : (
          <table className="loans-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Cliente</th>
                <th>Teléfono</th>
                <th>Monto</th>
                <th>Interés %</th>
                <th>Próxima fecha</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredLoans.map((loan, idx) => (
                <tr key={loan.id}>
                  <td>{idx + 1}</td>
                  <td><strong>{loan.clientName}</strong></td>
                  <td>{loan.clientPhone || '-'}</td>
                  <td>${(loan.amount / 1000).toFixed(0)}K</td>
                  <td><strong>{loan.interestRate}%</strong></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatearFecha(loan.maturityDate)}
                    {/* La misma columna guarda dos cosas distintas: en un
                        préstamo con plazo es el FIN; en uno abierto es el
                        cobro del interés. Llamarlas igual sería mentir, y el
                        que cobra necesita saber cuál está mirando. */}
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {loan.abierto ? 'cobro de interés' : 'fin del préstamo'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{getStatusIcon(loan.status)}</span>
                      <span style={{ color: getStatusColor(loan.status), fontWeight: 'bold', fontSize: '13px' }}>
                        {loan.status === 'overdue' && `Vencido hace ${Math.abs(loan.daysUntil)} días`}
                        {loan.status === 'warning' && `Vence en ${loan.daysUntil} días`}
                        {loan.status === 'active' && `Vence en ${loan.daysUntil} días`}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default OverdueLoans;
